import { useEffect, useRef, useState } from "react";
import { STORAGE_KEYS, setSessionItem } from "../utils/storageKeys";
import { safeTrack } from "../observability/tracking/safeTrack";
import type { WaEmbeddedSignupEventPayload } from "../types/facebook";

interface WabaEventState {
  lastEvent?: WaEmbeddedSignupEventPayload;
  error: string | null;
}

const SUCCESS_EVENT_TYPES = new Set([
  "FINISH",
  "FINISH_ONLY_WABA",
  "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
]);

/**
 * Type guard for Facebook's WA_EMBEDDED_SIGNUP postMessage payloads.
 *
 * Facebook's embedded signup SDK sends various message types through postMessage.
 * We only care about WA_EMBEDDED_SIGNUP events to avoid reacting to unrelated
 * Facebook SDK messages (like login state changes or plugin lifecycle events).
 */
function isWaEmbeddedSignupPayload(
  data: unknown,
): data is WaEmbeddedSignupEventPayload {
  if (!data || typeof data !== "object") return false;
  const maybe = data as WaEmbeddedSignupEventPayload;
  return typeof maybe.type === "string" && maybe.type === "WA_EMBEDDED_SIGNUP";
}

export function classifyEmbeddedSignupEvent(
  payload: WaEmbeddedSignupEventPayload,
): string {
  if (payload.event === "ERROR") {
    return "error";
  }

  if (payload.event === "CANCEL") {
    return "cancelled";
  }

  if (!SUCCESS_EVENT_TYPES.has(payload.event ?? "")) {
    return "in_progress";
  }

  if (payload?.data?.phone_number_id) {
    return "complete_success";
  }

  if (payload?.data?.waba_id) {
    return "incomplete_missing_phone";
  }

  return "incomplete_no_data";
}

/**
 * Manages Facebook WhatsApp Business Account embedded signup flow via postMessage.
 *
 * **Why postMessage**: Facebook's embedded signup runs in an iframe. The only way to
 * receive signup progress/completion events is through cross-origin postMessage.
 *
 * **Session persistence rationale**: Users may refresh the page or navigate away during
 * multi-step signup. By persisting WABA/phone/business IDs to sessionStorage immediately,
 * we can resume their flow or pre-populate confirmation screens on return.
 *
 * **Origin validation**: Without strict origin checking, malicious sites could send
 * fake completion events. We explicitly allowlist facebook.com domains to prevent
 * account takeover or unauthorized API calls with fabricated IDs.
 *
 * **Event tracking**: All Facebook events are tracked to enable debugging of signup
 * failures and to provide visibility into where the flow succeeds or fails.
 */
export function useWabaEmbeddedSignupEvents(): WabaEventState {
  const [error, setError] = useState<string | null>(null);
  const [lastEvent, setLastEvent] = useState<
    WaEmbeddedSignupEventPayload | undefined
  >(undefined);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    // Explicit origin allowlist prevents CSRF-style attacks where malicious sites
    // could send fake signup completion events. Facebook may send events from either
    // www or web subdomain depending on user's login state.
    const allowedOrigins = new Set([
      "https://www.facebook.com",
      "https://web.facebook.com",
    ]);

    const onMessage = (event: MessageEvent) => {
      try {
        if (!allowedOrigins.has(event.origin)) return;

        // Facebook SDK inconsistently sends either JSON strings or parsed objects
        // depending on the event type and SDK version. Accept both formats.
        let raw: unknown = event.data;
        if (typeof raw === "string") {
          try {
            raw = JSON.parse(raw);
          } catch {
            /* ignore parse error */
          }
        }
        if (!isWaEmbeddedSignupPayload(raw)) return;
        const payload = raw;

        // Track all events to identify funnel drop-off points. Boolean flags avoid
        // accidentally logging sensitive PII if Facebook changes payload structure.
        const eventClassification = classifyEmbeddedSignupEvent(payload);

        safeTrack("wa_embedded_signup_event", {
          event: payload.event,
          has_code: !!payload.code,
          has_waba_id: !!payload?.data?.waba_id,
          has_phone_number_id: !!payload?.data?.phone_number_id,
          has_business_id: !!payload?.data?.business_id,
          current_step: payload?.data?.current_step,
          event_classification: eventClassification,
        });

        // Persist IDs immediately on receipt, not just on completion. Facebook's
        // multi-step flow can be interrupted by user navigation or browser crashes.
        // Session storage allows us to resume or show "already registered" messaging.
        if (payload.data) {
          const { phone_number_id, waba_id, business_id } =
            payload.data as Record<string, string | undefined>;
          if (phone_number_id)
            setSessionItem(STORAGE_KEYS.phoneNumberId, phone_number_id);
          if (waba_id) setSessionItem(STORAGE_KEYS.wabaId, waba_id);
          if (business_id) setSessionItem(STORAGE_KEYS.businessId, business_id);
        }

        if (payload.event === "ERROR") {
          setError("Facebook signup reported an error. Please try again.");
        }
        setLastEvent(payload);
      } catch (e) {
        // Intentionally swallow errors in production. Malformed Facebook messages
        // should not crash the app or alert users — they may receive dozens of
        // unrelated postMessages from other browser extensions or embedded content.
        // Dev-only logging helps troubleshoot SDK changes.
        if (process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.warn("Malformed WA_EMBEDDED_SIGNUP message", e);
        }
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("message", onMessage);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("message", onMessage);
      }
      mountedRef.current = false;
    };
  }, []);

  return { lastEvent, error };
}
